﻿import { FunctionComponent, MutableRefObject, RefObject, useCallback, useEffect, useRef } from "react";
import {
    AlertDialog,
    AlertDialogBody,
    AlertDialogCloseButton,
    AlertDialogContent,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogOverlay,
    Box,
    Button,
    ButtonProps,
    useDisclosure,
} from "@chakra-ui/react";

export interface ConfirmProps {
    title?: string;
    prompt?: string | JSX.Element;
    yesButtonText?: string;
    yesButtonProps?: ButtonProps;
    noButtonText?: string;
    noButtonProps?: ButtonProps;
    openFnRef: MutableRefObject<(() => void) | undefined>;
    onResultRef: MutableRefObject<((value: boolean | PromiseLike<boolean>) => void) | undefined>;
}

/**
 * Disclaimer: This was created to be used by the useConfirm hook. It might be be awkward to use another way.
 * @param title
 * @param prompt
 * @param yesButtonText
 * @param yesButtonProps
 * @param noButtonText
 * @param noButtonProps
 * @param openFnRef
 * @param onResultRef
 * @constructor
 */
export const Confirm: FunctionComponent<ConfirmProps> = ({
    title = "Confirm",
    prompt = <Box>Are you sure?</Box>,
    yesButtonText = "Yes",
    yesButtonProps,
    noButtonText = "No",
    noButtonProps,
    openFnRef,
    onResultRef,
}) => {
    const { isOpen: isConfirmDeleteOpen, onOpen: onConfirmOpen, onClose: onConfirmClose } = useDisclosure();
    const confirmCancelRef = useRef<HTMLButtonElement>() as RefObject<HTMLButtonElement>;

    const open = useCallback(() => {
        onConfirmOpen();
    }, [onConfirmOpen]);

    const yes = useCallback(() => {
        if (onResultRef.current) {
            onResultRef.current(true);
        }
        onConfirmClose();
    }, [onResultRef, onConfirmClose]);

    const no = useCallback(() => {
        if (onResultRef.current) {
            onResultRef.current(false);
        }
        onConfirmClose();
    }, [onResultRef, onConfirmClose]);

    useEffect(() => {
        openFnRef.current = open;
    }, [openFnRef, open]);

    return (
        <AlertDialog motionPreset="slideInBottom" leastDestructiveRef={confirmCancelRef} onClose={no} isOpen={isConfirmDeleteOpen} isCentered>
            <AlertDialogOverlay />
            <AlertDialogContent>
                <AlertDialogHeader>{title}</AlertDialogHeader>
                <AlertDialogCloseButton />
                <AlertDialogBody>{prompt}</AlertDialogBody>
                <AlertDialogFooter>
                    <Button colorScheme="red" {...yesButtonProps} onClick={yes}>
                        {yesButtonText}
                    </Button>
                    <Button ml={"1rem"} {...noButtonProps} ref={confirmCancelRef} onClick={no}>
                        {noButtonText}
                    </Button>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
};
